import { BarChart3, RotateCcw, Table2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ErrorBoundary, IconButton, Tooltip } from '../../../shared/ui';

export interface TableSpec {
  caption: string;
  columns: readonly string[];
  rows: readonly { label: string; values: readonly string[] }[];
}

export interface ChartCardProps {
  title: string;
  subtitle?: string | undefined;
  legend?: ReactNode;
  /** Tabular alternative ("View as table"), for accessibility and audit. */
  table?: TableSpec | undefined;
  actions?: ReactNode;
  children: ReactNode;
}

export function ChartCard({ title, subtitle, legend, table, actions, children }: ChartCardProps) {
  const { t } = useTranslation();
  const [asTable, setAsTable] = useState(false);
  return (
    <section className="rounded-lg border border-border-subtle bg-bg-1 px-4 pt-3 pb-2">
      <header className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-medium">{title}</h2>
          {subtitle && <span className="text-xs text-text-3">{subtitle}</span>}
        </div>
        <div className="flex items-center gap-3">
          {legend}
          {actions}
          {table && (
            <Tooltip content={asTable ? t('charts.viewAsChart') : t('charts.viewAsTable')}>
              <IconButton
                size="sm"
                label={asTable ? t('charts.viewAsChart') : t('charts.viewAsTable')}
                aria-pressed={asTable}
                onClick={() => {
                  setAsTable((v) => !v);
                }}
              >
                {asTable ? <BarChart3 /> : <Table2 />}
              </IconButton>
            </Tooltip>
          )}
        </div>
      </header>
      <ErrorBoundary
        fallback={(reset) => (
          <div role="alert" className="flex flex-col items-center gap-2 py-10 text-sm text-text-2">
            {t('errors.chartFailed')}
            <Button size="sm" leadingIcon={<RotateCcw />} onClick={reset}>
              {t('errors.tryAgain')}
            </Button>
          </div>
        )}
      >
        {asTable && table ? <DataTable spec={table} /> : children}
      </ErrorBoundary>
    </section>
  );
}

function DataTable({ spec }: { spec: TableSpec }) {
  return (
    <div className="max-h-80 overflow-auto pb-2">
      <table className="w-full border-collapse text-xs">
        <caption className="sr-only">{spec.caption}</caption>
        <thead className="sticky top-0 bg-bg-1">
          <tr>
            {spec.columns.map((c, i) => (
              <th
                key={c}
                scope="col"
                className={`border-b border-border-subtle px-2 py-1.5 font-medium text-text-3 ${i === 0 ? 'text-left' : 'text-right'}`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spec.rows.map((row) => (
            <tr key={row.label} className="hover:bg-bg-3">
              <th scope="row" className="px-2 py-1 text-left font-normal text-text-2">
                {row.label}
              </th>
              {row.values.map((v, i) => (
                <td key={i} className="tabular px-2 py-1 text-right">
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
